import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { EditorContent, type JSONContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Chip from '@mui/material/Chip';

import {Flex, FlexColumn} from 'src/components/flex';
import { getSuggestion } from 'src/pages/landlord/playbooks/components/form/playbook-template-message/suggestion';
import { formatVariableName } from 'src/common/playbook-variables/playbook-variable-utils';
import { usePlaybookEditorContext } from 'src/pages/landlord/playbooks/components/editor/PlaybookEditorProvider';
import { getValidPlaybookVariablesV2 } from 'src/pages/landlord/playbooks/components/form/playbook-template-message/get-valid-playbook-variables';
import PlaybookWarning from 'src/pages/landlord/playbooks/components/PlaybookWarning';

import styles from 'src/pages/landlord/playbooks/components/form/playbook-template-message/PlaybookTemplateMessage.module.css';

type Props = {
    value: string;
    onChange: (value: string) => void;
    error?: boolean;
}

/**
 * Uses the Tiptap editor. For more information, see https://tiptap.dev/
 */
export default function PlaybookTemplateMessage({
    value,
    onChange,
    error,
}: Props) {
    const { playbookVariables } = usePlaybookEditorContext();

    const [isInit, setIsInit] = useState(false);

    const filteredVariables = useMemo(() => {
        return getValidPlaybookVariablesV2(playbookVariables);
    }, [playbookVariables]);

    const extensions = useMemo(() => [
        StarterKit,
        Mention.configure({
            HTMLAttributes: {
                class: 'mention',
            },
            suggestion: getSuggestion(filteredVariables),
            renderText: ({ node }) => `{${formatVariableName(node.attrs.id)}}`,
            renderHTML: ({ node }) => ['span', { class: 'mention' }, `{${formatVariableName(node.attrs.id)}}`]
        }),
    ], [filteredVariables]);

    const editor = useEditor({
        extensions,
        onUpdate: (updatedProps) => {
            onChange(updatedProps.editor.getText());
        },
    }, [extensions]);

    useEffect(() => {
        if (!editor || !filteredVariables || isInit) {
            return;
        }

        const parsed = parseText(value || '');
        editor.commands.setContent(parsed);
        setIsInit(true);
    }, [editor, isInit, filteredVariables, setIsInit, value]);

    const handleAddVariable = (variable: string) => () => {
        if (!editor) {
            return;
        }

        editor.commands.insertContentAt(
            editor.state.selection.anchor,
            { type: 'mention', attrs: { id: variable } }
        );
    };

    return (
        <FlexColumn>
            <div className={clsx(styles.root, error && styles.rootError)}>
                <div className={styles.border} />
                <div>
                    <EditorContent editor={editor} />

                    <Flex className={styles.toolbar} flexWrap="wrap" >
                        {filteredVariables.map(variable => (
                            <Chip
                                key={variable}
                                label={`{${formatVariableName(variable)}}`}
                                onClick={handleAddVariable(variable)}
                                size="small"
                            />
                        ))}
                    </Flex>
                </div>

            </div>
            <PlaybookWarning message={value} />
        </FlexColumn>
    );
}

/**
 * We need to parse the text to be able to display it in the editor.
 * In the backend, the text is stored as plain text, so we need to convert it to JSONContent.
 * The editor only accepts JSONContent.
 */
export const parseText = (text: string): JSONContent => {
    const matches = text.match(/{[^}]+}/g) || [text];

    const result = [];
    let lastIndex = 0;
    for (const match of matches) {
        const index = text.indexOf(match, lastIndex);
        if (index > lastIndex) {
            result.push(text.slice(lastIndex, index));
        }
        result.push(match);
        lastIndex = index + match.length;
    }

    if (lastIndex < text.length) {
        result.push(text.slice(lastIndex));
    }

    const newResult = [];

    // take all new lines and put them as separate items in the result array
    for (const part of result) {
        const parts = part.split('\n');
        if (parts.length === 1) {
            newResult.push(part);
        } else {
            newResult.push(
                ...parts
                    .map((part2, index) => {
                        return index === parts.length - 1 ? part2 : [part2, '\n'];
                    })
                    .flat()
                    .filter(Boolean)
            );
        }
    }

    return {
        type: 'doc',
        content: newResult.map((part) => {
            if (part.startsWith('{') && part.endsWith('}')) {
                return {
                    type: 'mention',
                    attrs: {
                        id: part.slice(1, -1),
                    },
                };
            }

            if (part === '\n') {
                return {
                    type: 'hardBreak',
                };
            }

            return {
                type: 'text',
                text: part,
            };
        }),
    };
};
